import * as React from "react"
import { cn } from "@/utils/util"

interface InputMainProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string
  leftIcon?: any
  rightIcon?: any
  leftAction?: () => void
  rightAction?: () => void
  type?: string
  inputSize?: 'default' | 'xs' | 'sm' | 'lg' | 'icon';
  inputClassName?: string
  color?: string
}

const Input = React.forwardRef<HTMLInputElement, InputMainProps>(({ className, leftIcon, rightIcon, leftAction, rightAction, type, inputSize = 'default', inputClassName, color, ...props }, ref) => {
  const IconLeftComponent = leftIcon
  const IconRightComponent = rightIcon

  const getInputColorClasses = () => {
    if (!color) return 'border-input focus:outline-none focus:border-input focus:ring-0 focus:shadow-none'

    switch (color) {
    case 'blue-500':
      return 'border-input focus:outline-none focus:border-blue-500 focus:ring-0 focus:shadow-none'
    case 'red-500':
      return 'border-input focus:outline-none focus:border-red-500 focus:ring-0 focus:shadow-none'
    case 'pink-500':
      return 'border-input focus:outline-none focus:border-pink-500 focus:ring-0 focus:shadow-none'
    case 'purple-500':
      return 'border-input focus:outline-none focus:border-purple-500 focus:ring-0 focus:shadow-none'
    case 'green-500':
      return 'border-input focus:outline-none focus:border-green-500 focus:ring-0 focus:shadow-none'
    case 'amber-500':
      return 'border-input focus:outline-none focus:border-amber-500 focus:ring-0 focus:shadow-none'
    default:
      return 'border-input focus:outline-none focus:border-blue-500 focus:ring-0 focus:shadow-none'
    }
  }

  const getIconColorClasses = () => {
    if (!color) return 'text-muted-foreground hover:text-foreground transition-colors'

    switch (color) {
    case 'blue-500':
      return 'text-muted-foreground hover:text-blue-500 transition-colors'
    case 'red-500':
      return 'text-muted-foreground hover:text-red-500 transition-colors'
    case 'pink-500':
      return 'text-muted-foreground hover:text-pink-500 transition-colors'
    case 'purple-500':
      return 'text-muted-foreground hover:text-purple-500 transition-colors'
    case 'green-500':
      return 'text-muted-foreground hover:text-green-500 transition-colors'
    case 'amber-500':
      return 'text-muted-foreground hover:text-amber-500 transition-colors'
    default:
      return 'text-muted-foreground hover:text-blue-500 transition-colors'
    }
  }

  const baseInputClasses = `flex w-full z-0 rounded-md border bg-background ring-offset-background sm:text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors ${getInputColorClasses()}`

  if (leftIcon || rightIcon) {
    return (
      <div className={cn("relative", className)} >
        {leftIcon && (
          <div className={`absolute left-[1px] top-[1px] z-10 ${inputSize == 'sm' ? 'p-1.5' : inputSize == 'lg' ? 'p-3' : 'p-2.5'} ${leftAction ? 'cursor-pointer' : ''} ${getIconColorClasses()}`} onClick={leftAction}>
            <IconLeftComponent className="h-[18px] w-[18px]" />
          </div>
        )}
        <input
          {...props}
          type={type}
          ref={ref}
          className={cn(
            baseInputClasses,
            inputSize == 'sm' ? 'h-8 px-2 py-1' : inputSize == 'lg' ? 'h-12 px-4 py-3' : 'h-10 px-3 py-2',
            leftIcon && (inputSize == 'sm' ? 'pl-[30px]' : inputSize == 'lg' ? 'pl-[42px]' : 'pl-9'),
            rightIcon && (inputSize == 'sm' ? 'pr-[30px]' : inputSize == 'lg' ? 'pr-[42px]' : 'pr-9'),
            inputClassName
          )}
        />
        {rightIcon && (
          <div className={`absolute right-[1px] top-[1px] z-10 ${inputSize == 'sm' ? 'p-1.5' : inputSize == 'lg' ? 'p-3' : 'p-2.5'} ${rightAction ? 'cursor-pointer' : ''} ${getIconColorClasses()}`} onClick={rightAction}>
            <IconRightComponent className="h-[18px] w-[18px]" />
          </div>
        )}
      </div>
    )
  }

  return (
    <input
      type={type}
      className={cn(
        baseInputClasses,
        inputSize == 'sm' ? 'h-8 px-2 py-1' : inputSize == 'lg' ? 'h-12 px-4 py-3' : 'h-10 px-3 py-2',
        inputClassName,
        className
      )}
      ref={ref}
      {...props}
    />
  )
})

Input.displayName = "Input"

export { Input }